<template>
  <div class="b-import pb-8">
    <div class="b-dialog__top-panel">
      <h2>{{ title }}</h2>
      <button
        class="b-btn b-btn_border_none"
        @click.prevent="close"
      >
        <dynamic-icon icon="close" />
      </button>
    </div>
    <div class="b-import__panel b-form px-8 mt-4">
      <div
        v-if="importType === 'currencyRate'"
        class="row mt-7 b-form__row_type_flex b-form__row_type_flex-end"
      >
        <div class="col-7">
          <toggle-switch
            :items="importTypes"
            :picked="importTypeForToggle"
            item-key="type"
            item-name="name"
            @click="clickOnImportType"
          />
        </div>
      </div>
      <div
        v-if="importType === 'currencyRate'"
        class="row mt-2 b-form__row_type_flex b-form__row_type_flex-end"
      >
        <div class="col-4 mb-8">
          <cool-select
            v-if="importTypeForToggle === 'import_update'"
            v-model="currentImportCourseId"
            :list="importedCourseNames"
            option-value="value"
            option-text="name"
            :placeholder="$t('CONTENT.choose')"
          />
          <input
            v-if="importTypeForToggle === 'import_create'"
            v-model="createImportCourseName"
            type="text"
            class="b-input"
          >
        </div>
      </div>
      <div class="b-import__save-template mb-4">
        <dynamic-icon
          class="mr-1"
          icon="info"
        />
        <div class="mr-1">
          {{ $t('CONTENT.importData.import_template_text') }}
        </div>
        <button
          class="b-import__template-btn"
          @click="downloadTemplate"
        >
          {{ $t('CONTENT.importData.import_template_btn') }}
        </button>
      </div>
      <div
        class="row b-import__panel-btn"
        :class="{ 'b-import__panel-btn--disabled': importType === 'currencyRate' && !currentImportCourseId && !createImportCourseName}"
        @click="checkedEmptyName"
      >
        <file-upload
          :import-type="importType"
          :course-name="createImportCourseName"
          :course-id="currentImportCourseId"
          @getFile="onGetFile"
          @finishUpload="onFinishUpload"
          @uploadError="onUploadError"
        />
      </div>
      <div
        v-if="importImported"
        class="mt-4"
      >
        <div class="b-server-error b-server-error_type_imported">
          <div class="b-server-error__icon-wrap b-server-error__icon-wrap_type_imported p-4">
            <dynamic-icon icon="check" />
          </div>
          <div class="b-server-error__text-wrap px-4">
            {{ $t('CONTENT.importData.records_uploaded_successfully') }}:
            {{ importImported }}
          </div>
        </div>
      </div>
      <div
        v-if="importDuplicateErrors.length > 0"
        class="mt-2"
      >
        <div class="b-server-error mb-4 b-server-error_type_duplicate">
          <div class="b-server-error__icon-wrap b-server-error__icon-wrap_type_duplicate p-4">
            <dynamic-icon icon="warning" />
          </div>
          <div class="b-server-error__inner-wrap">
            <div class="b-server-error__text-wrap px-4">
              {{ $t('CONTENT.importData.import_duplicate') }}:
              {{ importDuplicateErrors.length }}
            </div>
            <button
              class="b-btn b-btn_type_empty px-0 mr-2"
              @click="isShowDuplicateList = !isShowDuplicateList"
            >
              <dynamic-icon
                class="b-btn__icon"
                icon="arrow-down"
                :class="{'is-open': isShowDuplicateList}"
              />
            </button>
          </div>
        </div>
        <transition name="fade">
          <div v-if="isShowDuplicateList">
            <div
              v-for="(duplicateError, index) in importDuplicateErrors"
              :key="index"
              class="mb-4 b-server-error__icon-row b-server-error__icon-row_type_duplicate"
            >
              <div class="mr-2">
                <dynamic-icon icon="warning" />
              </div>
              <div>{{ buildErrorMessage(duplicateError) }}</div>
            </div>
          </div>
        </transition>
      </div>
      <div
        v-if="importErrors.length > 0"
        class="mt-2"
      >
        <div class="b-server-error mb-4">
          <div class="b-server-error__icon-wrap b-server-error__icon-wrap_type_error p-4">
            <dynamic-icon icon="warning" />
          </div>
          <div class="b-server-error__text-wrap px-4">
            {{ $t('CONTENT.importData.import_errors') }}:
            {{ importErrors.length }}
          </div>
        </div>
        <div
          v-for="(error, index) in importErrors"
          :key="index"
          class="mb-4 b-server-error__icon-row b-server-error__icon-row_type_error"
        >
          <div class="mr-2">
            <dynamic-icon icon="warning" />
          </div>
          <div><span v-html="buildErrorMessage(error)" /></div>
        </div>
      </div>
      <div
        v-if="showNewDiscountRateNotification"
      >
        <new-discount-rate-notification
          :save-contracts="saveContracts"
          :contracts-for-recalculation-download-excel="contractsForRecalculationDownloadExcel"
          :cancel-save="cancelSave"
        />
      </div>
    </div>
    <div class="px-2">
      <server-error :error="error" />
    </div>
  </div>
</template>

<script>
import downloadFileMixin from '../../mixins/downloadFile';
import buildErrorMessage from '../../mixins/buildErrorMessage';
import Spoiler from '../shared/Spoiler';
import DiscountRateAPI from '../../plugins/discountRates/api/DiscountRateAPI';
import NewDiscountRateNotification from '../NewDiscountRateNotification.vue';

export default {
  components: {
    NewDiscountRateNotification,
    Spoiler,
  },
  mixins: [downloadFileMixin, buildErrorMessage],
  props: {
    importedCourseNames: {
      type: Array,
      default() {
        return [];
      },
    },
    importType: {
      required: true,
    },
  },
  data() {
    return {
      title: null,
      error: null,
      importErrors: [],
      importDuplicateErrors: [],
      importContractsWithNewDiscountRates: [],
      contractsWithNewDiscountRates: null,
      importImported: 0,
      isShowDuplicateList: false,
      importTypeForToggle: 'import_update',
      file: null,
      importTypes: [
        {
          name: this.$t('CONTENT.importData.import_update'),
          type: 'import_update',
        },
        {
          name: this.$t('CONTENT.importData.import_create'),
          type: 'import_create',
        },
      ],
      currentImportCourseId: null,
      createImportCourseName: null,
      showNewDiscountRateNotification: false,
    };
  },
  created() {
    this.url = `${window.location.hash}/templates/${this.importType}_template.xlsx`;
    this.title = this.$t(`CONTENT.importData.import_${this.importType}_title`);
  },
  methods: {
    saveContracts() {
      this.saveExcelImportWithNewDiscountRates();
    },
    cancelSave() {
      this.$emit('hideFileInfo');
      this.showNewDiscountRateNotification = false;
    },
    checkedEmptyName(e) {
      if (this.importType === 'currencyRate' && !this.currentImportCourseId && !this.createImportCourseName) {
        e.preventDefault();
      }
    },
    clickOnImportType(type) {
      this.currentImportCourseId = null;
      this.createImportCourseName = null;
      this.importTypeForToggle = type;
    },
    async contractsForRecalculationDownloadExcel() {
      const originalImportType = this.importType;
      this.importType = 'importContractsWithNewDiscountRates';
      await this.downloadTemplate();
      this.importType = originalImportType;
    },
    async saveExcelImportWithNewDiscountRates() {
      try {
        this.$fileLoadingManager.startLoading();
        const api = new DiscountRateAPI();
        const newResponse = await api.saveExcelImportWithNewDiscountRates(this.file);
        await this.onFinishUpload(newResponse);
      } catch (error) {
        console.error(error);
      }
      this.$fileLoadingManager.finishLoading();
    },
    async onGetFile(file) {
      this.file = file;
    },
    async onFinishUpload(response) {
      this.error = null;
      this.importErrors = response.errors.exceptions;
      this.importDuplicateErrors = response.duplicateErrors.exceptions;
      this.importImported = response.imported;
      this.importContractsWithNewDiscountRates = response.discountRateContractsDto.contracts;
      this.contractsWithNewDiscountRates = response.discountRateContractsDto;
      this.showNewDiscountRateNotification = this.importContractsWithNewDiscountRates.length > 0;
      if (this.importTypeForToggle === 'import_create') {
        const createdCourse = response.result;
        this.importedCourseNames.push({
          name: createdCourse.name,
          value: createdCourse.fileid,
        });
      }

      if (this.importImported !== 0) {
        if (this.importType === 'counterparty') {
          await this.$store.dispatch('getAllCounterparties');
        }
        if (this.importType === 'counterpartyVat') {
          await this.$store.dispatch('getAllCounterparties');
        }
        if (this.importType === 'contract') {
          this.$store.dispatch('getAllContractAggregates');
        }
        if (this.importType === 'discountRate') {
          await this.$store.dispatch('discountRatesPlugin/loadAllDiscountRates');
        }
        if (this.importType === 'currency') {
          await this.$store.dispatch('loadAllCurrencyCode');
        }
      }
    },
    onUploadError(error) {
      if (error !== undefined) {
        this.error = error;
      }
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>
